import { useEffect, useRef, useState } from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  initializeMap,
  addGeoJSONSource,
  addOperationalPointsLayer,
  addRailwayLinesLayer,
  addHighlightLayer,
  highlightFeature,
  clearHighlight,
  fitToBounds
} from '@services/map.service';
import { getBounds } from '@utils/geojson.utils';
import type { GeoJSONFeatureCollection, FeatureProperties } from '@/types';

interface MapViewProps {
    coverageData: GeoJSONFeatureCollection;
    highlightedFeatureId: string | null;
    onFeatureClick: (feature: FeatureProperties | null) => void;
    onFeatureHover: (feature: FeatureProperties | null, position: { lng: number; lat: number } | null) => void;
    interactionEnabled?: boolean;
}

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * MapView component - Renders Mapbox GL JS map with railway network visualization
 */
export default function MapView({
  coverageData,
  highlightedFeatureId,
  onFeatureClick,
  onFeatureHover,
  interactionEnabled = true
}: MapViewProps): JSX.Element {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const [mapLoaded, setMapLoaded] = useState<boolean>(false);

  // Keep latest callbacks and props in refs so the mount-only effect stays stable
  const onFeatureClickRef = useRef(onFeatureClick);
  const onFeatureHoverRef = useRef(onFeatureHover);
  const interactionEnabledRef = useRef(interactionEnabled);
  const coverageDataRef = useRef(coverageData);

  useEffect(() => { onFeatureClickRef.current = onFeatureClick; }, [onFeatureClick]);
  useEffect(() => { onFeatureHoverRef.current = onFeatureHover; }, [onFeatureHover]);
  useEffect(() => { interactionEnabledRef.current = interactionEnabled; }, [interactionEnabled]);
  useEffect(() => { coverageDataRef.current = coverageData; }, [coverageData]);

  // Initialize map on mount
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    try {
      const map = initializeMap(mapContainerRef.current, {
        accessToken: import.meta.env.VITE_MAPBOX_TOKEN || ''
      });

      mapRef.current = map;

      map.on('load', () => {
        // Add data sources
        addGeoJSONSource(map, 'railway-coverage', coverageDataRef.current);

        // Add layers
        addRailwayLinesLayer(map, 'railway-coverage', 'railway-lines');
        addOperationalPointsLayer(
          map,
          'railway-coverage',
          'operational-points'
        );
        addHighlightLayer(map, 'railway-coverage', 'highlight-layer');

        // Fit to data bounds
        const initialData = coverageDataRef.current;
        if (initialData && initialData.features.length > 0) {
          const bounds = getBounds(initialData);
          if (bounds) {
            fitToBounds(map, bounds as [number, number, number, number]);
          }
        }

        setMapLoaded(true);
      });

      // Click event
      map.on('click', ['operational-points', 'railway-lines'], (e: any) => {
        if (!interactionEnabledRef.current) return;

        if (e.features && e.features.length > 0) {
          const feature = e.features[0];
          onFeatureClickRef.current?.(feature.properties as FeatureProperties);
        }
      });

      // Hover events
      map.on('mouseenter', ['operational-points', 'railway-lines'], (e: any) => {
        if (e.features && e.features.length > 0) {
          map.getCanvas().style.cursor = interactionEnabledRef.current
            ? 'pointer'
            : 'not-allowed';
          const feature = e.features[0];
          onFeatureHoverRef.current?.(feature.properties as FeatureProperties, e.lngLat);
        }
      });

      map.on('mouseleave', ['operational-points', 'railway-lines'], () => {
        map.getCanvas().style.cursor = '';
        onFeatureHoverRef.current?.(null, null);
      });
    } catch (error) {
      console.error('Error initializing map:', error);
    }

    // Cleanup on unmount
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []); // Run once on mount

  // Update coverage data when it changes
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    const source = mapRef.current.getSource('railway-coverage');
    if (source) {
      source.setData(coverageData as GeoJSON.FeatureCollection);

      // Fit to new bounds
      if (coverageData && coverageData.features.length > 0) {
        const bounds = getBounds(coverageData);
        if (bounds) {
          fitToBounds(mapRef.current, bounds as [number, number, number, number]);
        }
      }
    }
  }, [coverageData, mapLoaded]);

  // Update highlighted feature
  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;

    if (highlightedFeatureId) {
      highlightFeature(mapRef.current, 'highlight-layer', highlightedFeatureId);
    } else {
      clearHighlight(mapRef.current, 'highlight-layer');
    }
  }, [highlightedFeatureId, mapLoaded]);

  // Update cursor based on interaction state
  useEffect(() => {
    if (!mapRef.current) return;

    const canvas = mapRef.current.getCanvas();
    if (!interactionEnabled) {
      canvas.style.cursor = 'not-allowed';
    } else {
      canvas.style.cursor = '';
    }
  }, [interactionEnabled]);

  return (
    <div className="map-view">
      <div ref={mapContainerRef} className="map-container" />
      {!interactionEnabled && (
        <div className="map-disabled-overlay">
          <div className="map-disabled-message">
            <p>Map interaction disabled while filter is active</p>
            <p className="map-disabled-hint">
              Clear the filter to interact with the map
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
